/**
 * Shared UI state for an OpenNebula service console. The key piece is
 * `isWaitingUpdate` — set it to the action you just dispatched (e.g. 'reboot')
 * and detail queries switch to adaptive polling until the transition settles.
 * Ported from @console Internal.Opennebula/utils/contexts, typed (no `any`).
 */
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { WaitingState } from './_shared';

interface OpennebulaContextValue {
  /** The action in flight (drives adaptive polling), or false when idle. */
  isWaitingUpdate: WaitingState;
  setWaiting: (w: WaitingState) => void;
  /** Last-fetched instance/resource payload, shared across detail tabs. */
  client: Record<string, unknown> | null;
  setClient: (c: Record<string, unknown> | null) => void;
  /** Active panel/section id within a detail page. */
  active: string | null;
  setActive: (a: string | null) => void;
}

const noop = () => {};

const OpennebulaContext = createContext<OpennebulaContextValue>({
  isWaitingUpdate: false,
  setWaiting: noop,
  client: null,
  setClient: noop,
  active: null,
  setActive: noop,
});

export function OpennebulaProvider({ children }: { children: ReactNode }) {
  const [isWaitingUpdate, setWaiting] = useState<WaitingState>(false);
  const [client, setClient] = useState<Record<string, unknown> | null>(null);
  const [active, setActive] = useState<string | null>(null);

  const value = useMemo(
    () => ({ isWaitingUpdate, setWaiting, client, setClient, active, setActive }),
    [isWaitingUpdate, client, active],
  );

  return <OpennebulaContext.Provider value={value}>{children}</OpennebulaContext.Provider>;
}

export function useOpennebula() {
  return useContext(OpennebulaContext);
}
